var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import BigNumber from 'bignumber.js';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MarketInfoData, MarketPortfolio, StatCard, TokenActionModal, MobileActionButtons, NotificationComp, useWindowSizes, convertToKAndM, getAutoSlippage,
// MarketData,
 } from 'ui-kit';
import { getIcon } from '../utils/getIcon';
import { generateSupplyTooltipItems, generateBorrowTooltipItems } from '../utils/tooltipItems';
import { IS_WIND_DOWN_ACTIVE, WIND_DOWN_MESSAGE } from '../utils/windDownConfig';
import { SupplyChart, BorrowChart, UtilizationChart } from '../components/MarketCharts';
import { useAlphalendSubscription } from '../hooks/useAlphalendSubscription';
import { useAlphalendEventSubscription } from '../hooks/useAlphalendEventSubscription';
import { useBalances } from '../context/BalanceContext';
import { getOraclePriceLink, marketsArray } from '../data/marketInfo';
import { alphalendSwapAndSupplyOrRepayTransactionHandler, alphalendTransactionHandler, alphalendFlashRepayTransactionHandler, } from '../utils/alphalendTransactionHandler';
import { useCurrentWallet, useSignAndExecuteTransaction } from '../hooks/walletCompat';
import { alphalendWebSocketService } from '../services/alphalendWebsocket';
import { usePosition } from '../context/PositionContext';
import SwapModal from '../components/SwapModal';
import { SwapButton } from '../components/SwapButton';
import { wbtc_layer0_coinType } from '../utils/coinDisplayUtils';
import { isMarketBorrowCapReached, isMarketSupplyCapReached } from '../utils/marketCapUtils';
import { useSubscription } from '../hooks/useSubscription';
import { toRepayAmountBaseUnits, getMaxRepayByCollateral } from '../utils/flashRepayUtils';
import { useNaviFlashLoanFee } from '../hooks/useNaviFlashLoanFee';
import { useAddressBalanceGuard } from '../hooks/useAddressBalanceGuard';
import { useFlashRepaySupported } from '../hooks/useFlashRepaySupported';
import { getSuppliedAndBorrowedCoinTypes, calculateTransactionAmount, isSwapNeeded, getBorrowedCoinTypes, } from '../utils/zapSwapUtils';
import { usePollingSwapQuote } from '../hooks/usePollingSwapQuote';
import { buildTokensList } from '../utils/walletBalanceHelpers';
const MarketDetails = ({ walletConnectFunctionality, themeName, address }) => {
    var _a, _b, _c, _d, _e;
    const navigate = useNavigate();
    const { symbol } = useParams();
    const location = useLocation();
    const marketValue = (_b = (_a = location.state) === null || _a === void 0 ? void 0 : _a.marketId) !== null && _b !== void 0 ? _b : (_c = marketsArray.find((m) => m.symbol === symbol)) === null || _c === void 0 ? void 0 : _c.id;
    const marketId = marketValue;
    const screenWidth = useWindowSizes();
    const { isSmallMobile, isLargeMobile } = screenWidth;
    const isMobile = screenWidth.width < 768;
    const isDark = themeName === 'dark';
    const { selectedPositionCapId: positionCapId } = usePosition();
    const { walletBalancesMap } = useBalances();
    const checkAddressBalanceConflict = useAddressBalanceGuard();
    const { currentWallet } = useCurrentWallet();
    const signAndExecuteTransaction = useSignAndExecuteTransaction();
    const tokensRef = useRef([]);
    const [activeTab, setActiveTab] = useState('Supply');
    const [isTransactionLoading, setIsTransactionLoading] = useState(false);
    const [resetInputTrigger, setResetInputTrigger] = useState(0);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
    const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
    const [notificationInfo, setNotificationInfo] = useState({
        title: '',
        description: '',
        link: '',
        image: undefined,
        spinner: false,
    });
    const { data: alphalendMarketDetailsData } = useAlphalendSubscription(`market_details:${marketId}`);
    const { data: marketHighFreqData } = useAlphalendEventSubscription('market_data', 'market_data_high_freq');
    const { data: marketLowFreqData } = useAlphalendEventSubscription('market_data', 'market_data_low_freq');
    const { data: portfolioLowFreqData } = useAlphalendEventSubscription(positionCapId ? `portfolio:${positionCapId}` : null, positionCapId ? `portfolio:${positionCapId}_low_freq` : null);
    const { data: portfolioHighFreqData } = useAlphalendEventSubscription(positionCapId ? `portfolio:${positionCapId}` : null, positionCapId ? `portfolio:${positionCapId}_high_freq` : null);
    const marketHigh = marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.markets[marketId];
    const marketLow = marketLowFreqData === null || marketLowFreqData === void 0 ? void 0 : marketLowFreqData.markets[marketId];
    const marketInfo = marketsArray.find((m) => m.id === marketId);
    const { data: coinInfoData } = useSubscription('coin_info');
    const [tokens, setTokens] = useState([]);
    const [selectedSwapSupplyToken, setSelectedSwapSupplyToken] = useState(null);
    const [swapAndPayAmount, setSwapAndPayAmount] = useState('');
    // const [marketTokensArray, setMarketTokensArray] = useState<MarketData[]>([]);
    const [swapQuoteObject, setSwapQuoteObject] = useState(null);
    const [slippageValue, setSlippageValue] = useState(0.5);
    const [isQuoteStale, setIsQuoteStale] = useState(false);
    const [isQuoteLoading, setIsQuoteLoading] = useState(false);
    const [selectedCollateralToken, setSelectedCollateralToken] = useState(null);
    // Flash repay quote states
    const [flashRepayQuoteObject, setFlashRepayQuoteObject] = useState(null);
    const [isFlashRepayQuoteStale, setIsFlashRepayQuoteStale] = useState(false);
    const [isFlashRepayQuoteLoading, setIsFlashRepayQuoteLoading] = useState(false);
    const [flashRepaySwapAmount, setFlashRepaySwapAmount] = useState('');
    const [useCollateral, setUseCollateral] = useState(false);
    const baseTokenData = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        const borrowCapReached = isMarketBorrowCapReached(marketLow);
        const supplyCapReached = isMarketSupplyCapReached(marketLow);
        const hasSupplyPosition = parseFloat(((_a = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.supplied_amounts) === null || _a === void 0 ? void 0 : _a[marketId]) || '0') > 0;
        const hasBorrowPosition = ((_b = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.borrowed_amounts) === null || _b === void 0 ? void 0 : _b[marketId]) !== undefined;
        const isRetired = (marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.retired) === true;
        return {
            marketId: marketId,
            symbol: symbol || '',
            icon: getIcon((marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.coinType) === wbtc_layer0_coinType
                ? 'wbtc_layer0'
                : symbol === null || symbol === void 0 ? void 0 : symbol.toLowerCase()),
            price: (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.price) || '0',
            // Wind-down mode closes supply and borrow everywhere. Withdraw and repay stay open.
            pausedTooltip: IS_WIND_DOWN_ACTIVE ? WIND_DOWN_MESSAGE : undefined,
            isSupplyEnabled: !IS_WIND_DOWN_ACTIVE && !hasBorrowPosition && !isRetired,
            isSupplyCapReached: !hasBorrowPosition && !isRetired && supplyCapReached,
            isBorrowEnabled: !IS_WIND_DOWN_ACTIVE && !hasSupplyPosition && !isRetired && !borrowCapReached,
            isBorrowCapReached: !hasSupplyPosition && !isRetired && borrowCapReached,
            totalSupply: (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.total_supply) || '0',
            totalBorrowed: (parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.total_supply) || '0') -
                parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.available_liquidity) || '0')).toString(),
            available: (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.available_liquidity) || '0',
            supplyApr: ((_c = marketLow === null || marketLow === void 0 ? void 0 : marketLow.supply_apr) === null || _c === void 0 ? void 0 : _c.total) || '0',
            borrowApr: ((_d = marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_apr) === null || _d === void 0 ? void 0 : _d.total) || '0',
            ltv: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.ltv) || '0',
            liquidationThreshold: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.liquidation_threshold) || '0',
            closeFactor: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.close_factor_percentage) || '0',
            spreadFee: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.spread_fee) || '0',
            borrowWeight: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_weight) || '0',
            borrowFee: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_fee) || '0',
            utilizationRate: Number(marketLow === null || marketLow === void 0 ? void 0 : marketLow.utilization_rate).toFixed(2) || '0',
            isSupplyRewardsEnabled: ((_e = marketLow === null || marketLow === void 0 ? void 0 : marketLow.supply_apr) === null || _e === void 0 ? void 0 : _e.rewards) && marketLow.supply_apr.rewards.length > 0,
            isBorrowRewardsEnabled: ((_f = marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_apr) === null || _f === void 0 ? void 0 : _f.rewards) && marketLow.borrow_apr.rewards.length > 0,
            inWallet: walletBalancesMap && marketInfo
                ? (parseFloat(walletBalancesMap.get(marketInfo.coinType) || '0') /
                    Math.pow(10, marketInfo.expo)).toString()
                : '0',
            inWalletUSD: walletBalancesMap && marketInfo
                ? ((parseFloat(walletBalancesMap.get(marketInfo.coinType) || '0') /
                    Math.pow(10, marketInfo.expo)) *
                    parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.price) || '0')).toString()
                : '0',
            allowedSupplyAmount: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.allowed_supply_amount) || '0',
            allowedBorrowAmount: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.allowed_borrow_amount) || '0',
            remainingInflowCapacity: (_g = marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.remaining_inflow_capacity) !== null && _g !== void 0 ? _g : null,
            remainingOutflowCapacity: (_h = marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.remaining_outflow_capacity) !== null && _h !== void 0 ? _h : null,
            interestRateKinks: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.interest_rate_kinks) || [],
            interestRates: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.interest_rates) || [],
            // Normalize to token's decimal precision (expo) to eliminate floating point noise
            // that would otherwise appear as dust in balance arithmetic downstream
            balance: new BigNumber(((_j = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.supplied_amounts) === null || _j === void 0 ? void 0 : _j[marketId]) || '0').toFixed((_k = marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.expo) !== null && _k !== void 0 ? _k : 9),
            balanceUSD: (parseFloat(((_l = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.supplied_amounts) === null || _l === void 0 ? void 0 : _l[marketId]) || '0') *
                parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.price) || '0')).toString(),
            supplyBalance: new BigNumber(((_m = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.supplied_amounts) === null || _m === void 0 ? void 0 : _m[marketId]) || '0').toFixed((_o = marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.expo) !== null && _o !== void 0 ? _o : 9),
            borrowedBalance: new BigNumber(((_p = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.borrowed_amounts) === null || _p === void 0 ? void 0 : _p[marketId]) || '0').toFixed((_q = marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.expo) !== null && _q !== void 0 ? _q : 9),
            supplyBalanceUSD: (parseFloat(((_r = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.supplied_amounts) === null || _r === void 0 ? void 0 : _r[marketId]) || '0') *
                parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.price) || '0')).toString(),
            borrowedBalanceUSD: (parseFloat(((_s = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.borrowed_amounts) === null || _s === void 0 ? void 0 : _s[marketId]) || '0') *
                parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.price) || '0')).toString(),
            safeBorrowLimit: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.safe_borrow_limit) || '0',
            borrowLimitUsed: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.borrow_limit_used) || '0',
            totalSuppliedUsd: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd) || '0',
            liquidationLimit: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.liquidation_limit) || '0',
            apr: ((_t = marketLow === null || marketLow === void 0 ? void 0 : marketLow.supply_apr) === null || _t === void 0 ? void 0 : _t.total) || '0',
            retired: isRetired,
            totalSupplySubText: (parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.total_supply) || '0') * parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.price) || '0')).toFixed(2),
            availableSubText: (parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.available_liquidity) || '0') * parseFloat((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.price) || '0')).toFixed(2),
            supplyToolTipItems: generateSupplyTooltipItems(marketLow === null || marketLow === void 0 ? void 0 : marketLow.supply_apr, symbol),
            borrowToolTipItems: generateBorrowTooltipItems(marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_apr, symbol),
        };
    }, [
        marketId,
        symbol,
        marketHigh,
        marketLow,
        walletBalancesMap,
        marketInfo,
        portfolioLowFreqData,
        portfolioHighFreqData,
    ]);
    const supplyChartData = useMemo(() => {
        var _a;
        return (((_a = alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.supply_apr_graph) === null || _a === void 0 ? void 0 : _a.map((item) => ({
            timestamp: item.timestamp,
            apr: item.apr,
            volume: item.volume,
        }))) || []);
    }, [alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.supply_apr_graph]);
    const borrowChartData = useMemo(() => {
        var _a;
        return (((_a = alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.borrow_apr_graph) === null || _a === void 0 ? void 0 : _a.map((item) => ({
            timestamp: item.timestamp,
            apr: item.apr,
            volume: item.volume,
        }))) || []);
    }, [alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.borrow_apr_graph]);
    const utilizationRateData = useMemo(() => {
        var _a;
        return (_a = alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.utilization_rate_graph) === null || _a === void 0 ? void 0 : _a.map((item) => ({
            utilizationRate: item.utilization_rate,
            borrowApr: item.borrow_apr,
        }));
    }, [alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.utilization_rate_graph]);
    const collateralTokens = useMemo(() => {
        if (!(portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.supplied_amounts) || !(marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.markets))
            return [];
        return Object.entries(portfolioLowFreqData.supplied_amounts)
            .filter(([, amt]) => parseFloat(amt) > 0)
            .flatMap(([mid, amt]) => {
            var _a, _b, _c, _d;
            const marketInfo = marketsArray.find((m) => m.id === mid);
            if (!marketInfo)
                return [];
            const price = ((_a = marketHighFreqData.markets[mid]) === null || _a === void 0 ? void 0 : _a.price) || '0';
            const balance = parseFloat(amt);
            const usdValueNum = balance * parseFloat(price);
            const marketLow = (_b = marketLowFreqData === null || marketLowFreqData === void 0 ? void 0 : marketLowFreqData.markets) === null || _b === void 0 ? void 0 : _b[mid];
            return [
                {
                    symbol: marketInfo.symbol,
                    icon: getIcon(marketInfo.coinType === wbtc_layer0_coinType
                        ? 'wbtc_layer0'
                        : marketInfo.symbol.toLowerCase()),
                    balance: amt,
                    price,
                    usdValueNum,
                    marketId: mid,
                    coinType: marketInfo.coinType,
                    expo: marketInfo.expo,
                    ltv: (_c = marketLow === null || marketLow === void 0 ? void 0 : marketLow.ltv) !== null && _c !== void 0 ? _c : '0',
                    liquidationThreshold: (_d = marketLow === null || marketLow === void 0 ? void 0 : marketLow.liquidation_threshold) !== null && _d !== void 0 ? _d : '0',
                },
            ];
        });
    }, [
        portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.supplied_amounts,
        marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.markets,
        marketLowFreqData === null || marketLowFreqData === void 0 ? void 0 : marketLowFreqData.markets,
    ]);
    const isFlashRepaySupported = useFlashRepaySupported(marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.coinType);
    const { feeDecimal: naviFlashLoanFee } = useNaviFlashLoanFee(marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.coinType);
    const flashRepayMaxRepayAmount = useMemo(() => {
        if (!(baseTokenData === null || baseTokenData === void 0 ? void 0 : baseTokenData.price) || !(baseTokenData === null || baseTokenData === void 0 ? void 0 : baseTokenData.borrowedBalance))
            return undefined;
        return getMaxRepayByCollateral(selectedCollateralToken !== null && selectedCollateralToken !== void 0 ? selectedCollateralToken : undefined, baseTokenData.price, baseTokenData.borrowedBalance, naviFlashLoanFee);
    }, [
        baseTokenData === null || baseTokenData === void 0 ? void 0 : baseTokenData.price,
        baseTokenData === null || baseTokenData === void 0 ? void 0 : baseTokenData.borrowedBalance,
        selectedCollateralToken,
        naviFlashLoanFee,
    ]);
    useEffect(() => {
        if (activeTab !== 'Repay' || collateralTokens.length === 0) {
            return;
        }
        const current = selectedCollateralToken;
        const isCurrentInList = current && collateralTokens.some((t) => t.marketId === current.marketId);
        if (!current || !isCurrentInList) {
            setSelectedCollateralToken(collateralTokens[0]);
        }
    }, [activeTab, collateralTokens, selectedCollateralToken]);
    // Set auto slippage when "use collateral" is toggled on or collateral token changes (Repay tab)
    const autoCollateralSymbol = (_d = (selectedCollateralToken !== null && selectedCollateralToken !== void 0 ? selectedCollateralToken : collateralTokens[0])) === null || _d === void 0 ? void 0 : _d.symbol;
    useEffect(() => {
        if (activeTab === 'Repay' && useCollateral && autoCollateralSymbol) {
            setSlippageValue(getAutoSlippage(autoCollateralSymbol, symbol !== null && symbol !== void 0 ? symbol : undefined));
        }
    }, [activeTab, useCollateral, symbol, autoCollateralSymbol]);
    const handleSubmit = (amount, action, displayAmount, _selectedToken, options) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!address) {
            console.error('Missing required data for transaction');
            return;
        }
        // Wind-down mode: never build a supply or borrow transaction.
        if (IS_WIND_DOWN_ACTIVE && (action === 'Supply' || action === 'Borrow')) {
            console.error(`${action} is paused while AlphaFi winds down`);
            return;
        }
        if (action === 'Supply' || action === 'Repay') {
            if (checkAddressBalanceConflict([
                { coinType: selectedSwapSupplyToken === null || selectedSwapSupplyToken === void 0 ? void 0 : selectedSwapSupplyToken.tokenId },
                { coinType: marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.coinType },
            ])) {
                setNotificationInfo({
                    title: 'Wallet Update Required',
                    description: `${(_a = currentWallet === null || currentWallet === void 0 ? void 0 : currentWallet.name) !== null && _a !== void 0 ? _a : 'Your wallet'} doesn't yet support address balances, a recent Sui network update. Your funds are safe — to continue, connect a supported wallet.`,
                    image: getIcon('failed'),
                    spinner: false,
                });
                setIsNotificationOpen(true);
                return;
            }
        }
        if (action === 'Repay' &&
            (options === null || options === void 0 ? void 0 : options.useCollateral) &&
            ((_b = options === null || options === void 0 ? void 0 : options.selectedCollateralToken) === null || _b === void 0 ? void 0 : _b.marketId) &&
            ((_c = options === null || options === void 0 ? void 0 : options.selectedCollateralToken) === null || _c === void 0 ? void 0 : _c.coinType) &&
            positionCapId &&
            marketInfo) {
            const numAmount = parseFloat(amount);
            const numMax = flashRepayMaxRepayAmount != null && flashRepayMaxRepayAmount !== ''
                ? parseFloat(flashRepayMaxRepayAmount)
                : Infinity;
            const cappedAmount = Number.isFinite(numAmount) && Number.isFinite(numMax)
                ? String(Math.min(numAmount, numMax))
                : amount;
            const borrowedStr = (_d = baseTokenData === null || baseTokenData === void 0 ? void 0 : baseTokenData.borrowedBalance) !== null && _d !== void 0 ? _d : '';
            const borrowedNum = parseFloat(borrowedStr);
            const isMaxRepay = borrowedStr &&
                Number.isFinite(borrowedNum) &&
                borrowedNum > 0 &&
                parseFloat(cappedAmount) >= borrowedNum * 0.9999;
            const amountToRepay = isMaxRepay ? borrowedStr : cappedAmount;
            const repayAmountBaseUnits = isMaxRepay
                ? undefined
                : toRepayAmountBaseUnits(amountToRepay, marketInfo.expo);
            const slippageDecimal = Number.isFinite(slippageValue) && slippageValue > 0
                ? Math.min(slippageValue / 100, 1)
                : 0.01;
            try {
                yield alphalendFlashRepayTransactionHandler({
                    positionCapId,
                    repayCoinType: marketInfo.coinType,
                    repayMarketId: marketId,
                    withdrawCoinType: options.selectedCollateralToken.coinType,
                    withdrawMarketId: options.selectedCollateralToken.marketId,
                    address,
                    slippage: slippageDecimal,
                    network: 'mainnet',
                    repayAmountBaseUnits,
                    signAndExecuteTransaction: signAndExecuteTransaction,
                    setNotificationInfo: setNotificationInfo,
                    setIsNotificationOpen: setIsNotificationOpen,
                    setIsTransactionLoading: setIsTransactionLoading,
                    resetInputValues: () => {
                        setResetInputTrigger((prev) => prev + 1);
                        if (isMobile) {
                            setIsMobileModalOpen(false);
                        }
                    },
                    notifyTransaction: (mId, posCapId, txType) => {
                        alphalendWebSocketService.sendTransactionNotification(mId, posCapId, txType);
                    },
                    notificationContext: {
                        repaySymbol: (_e = marketInfo.symbol) !== null && _e !== void 0 ? _e : 'debt',
                        withdrawSymbol: (_f = options.selectedCollateralToken.symbol) !== null && _f !== void 0 ? _f : 'collateral',
                        repayDisplayAmount: displayAmount,
                        isPartial: repayAmountBaseUnits != null,
                    },
                });
            }
            catch (err) {
                setIsTransactionLoading(false);
                setNotificationInfo({
                    title: 'Flash Repay Failed',
                    description: (_g = err === null || err === void 0 ? void 0 : err.message) !== null && _g !== void 0 ? _g : 'Failed to build or submit flash repay.',
                    spinner: false,
                });
                setIsNotificationOpen(true);
            }
            return;
        }
        const suiBalance = (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get('0x2::sui::SUI')) || '0';
        const suppliedAndBorrowedCoinTypes = getSuppliedAndBorrowedCoinTypes(portfolioLowFreqData, marketId);
        const borrowedCoinTypes = getBorrowedCoinTypes(portfolioLowFreqData);
        const transaction_amount = calculateTransactionAmount(swapQuoteObject, selectedSwapSupplyToken);
        let transaction_displayAmount = displayAmount;
        const defaultTokenForSwap = tokens.find((t) => baseTokenData !== null && t.symbol === baseTokenData.symbol);
        if (isSwapNeeded(selectedSwapSupplyToken, defaultTokenForSwap || undefined, swapQuoteObject)) {
            transaction_displayAmount = (swapQuoteObject === null || swapQuoteObject === void 0 ? void 0 : swapQuoteObject.payAmount) || '0';
            yield alphalendSwapAndSupplyOrRepayTransactionHandler({
                amount: transaction_amount,
                network: 'mainnet',
                action: activeTab === 'Supply' ? 'swap_supply' : 'swap_repay',
                modalState: {
                    isOpen: true,
                    token: baseTokenData,
                    actionType: action,
                },
                address: address,
                slippage: slippageValue,
                positionCapId: positionCapId,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setIsTransactionLoading,
                displayAmount: transaction_displayAmount,
                receivedAmountDisplay: swapQuoteObject === null || swapQuoteObject === void 0 ? void 0 : swapQuoteObject.receiveAmount,
                suiBalance: suiBalance,
                swappedFromTokenCoinType: (selectedSwapSupplyToken === null || selectedSwapSupplyToken === void 0 ? void 0 : selectedSwapSupplyToken.tokenId) || '',
                swappedFromTokenSymbol: (selectedSwapSupplyToken === null || selectedSwapSupplyToken === void 0 ? void 0 : selectedSwapSupplyToken.symbol) || '',
                swappedIntoTokenCoinType: (swapQuoteObject === null || swapQuoteObject === void 0 ? void 0 : swapQuoteObject.receiveToken.tokenId) || '',
                resetInputValues: () => {
                    setResetInputTrigger((prev) => prev + 1);
                    setSwapQuoteObject(null);
                    setSwapAndPayAmount('');
                    // handleCloseModal();
                },
                notifyTransaction: (marketId, positionCapId, txType) => {
                    alphalendWebSocketService.sendTransactionNotification(marketId, positionCapId, txType);
                },
                suppliedAndBorrowedCoinTypes: suppliedAndBorrowedCoinTypes,
                path: window.location.pathname,
            });
            return;
        }
        yield alphalendTransactionHandler({
            amount: amount,
            network: 'mainnet',
            action: action,
            modalState: {
                isOpen: true,
                token: baseTokenData,
                actionType: action,
            },
            address: address,
            positionCapId: positionCapId,
            signAndExecuteTransaction: signAndExecuteTransaction,
            setNotificationInfo: setNotificationInfo,
            setIsNotificationOpen: setIsNotificationOpen,
            setIsTransactionLoading: setIsTransactionLoading,
            displayAmount: displayAmount,
            suiBalance: suiBalance,
            resetInputValues: () => {
                setResetInputTrigger((prev) => prev + 1);
                if (isMobile) {
                    setIsMobileModalOpen(false);
                }
            },
            notifyTransaction: (mId, posCapId, txType) => {
                alphalendWebSocketService.sendTransactionNotification(mId, posCapId, txType);
            },
            suppliedAndBorrowedCoinTypes: suppliedAndBorrowedCoinTypes,
            path: window.location.pathname,
            borrowedCoinTypes: borrowedCoinTypes,
        });
    });
    const handleBackClick = () => {
        if (window.history.length > 1) {
            window.history.back();
        }
        else {
            navigate('/');
        }
    };
    const handleMobileSupplyClick = () => {
        setActiveTab('Supply');
        setIsMobileModalOpen(true);
    };
    const handleMobileBorrowClick = () => {
        setActiveTab('Borrow');
        setIsMobileModalOpen(true);
    };
    const handleMobileModalClose = () => {
        setIsMobileModalOpen(false);
    };
    const handleSwapNotification = (info) => {
        setNotificationInfo(info);
        setIsNotificationOpen(true);
    };
    const supplyData = () => {
        const totalSupply = parseFloat(baseTokenData.totalSupply);
        const allowedSupplyAmount = parseFloat(baseTokenData.allowedSupplyAmount);
        const maxSupply = parseFloat((marketLow === null || marketLow === void 0 ? void 0 : marketLow.deposit_limit) || '0');
        const totalBorrowed = parseFloat(baseTokenData.totalBorrowed);
        const price = parseFloat(baseTokenData.price);
        const supplyProgressPercentage = (totalSupply / maxSupply) * 100;
        const alphaSupplyProgressRaw = (totalSupply / (totalSupply + allowedSupplyAmount)) * 100;
        const alphaSupplyProgressPercentage = Number.isInteger(alphaSupplyProgressRaw)
            ? alphaSupplyProgressRaw
            : alphaSupplyProgressRaw.toFixed(1);
        return {
            type: 'supply',
            data: {
                symbol: baseTokenData.symbol,
                progressPercentage: baseTokenData.marketId === '12'
                    ? Number(alphaSupplyProgressPercentage)
                    : supplyProgressPercentage,
                totalAmount: baseTokenData.totalSupply,
                maxAmount: baseTokenData.marketId === '12'
                    ? (totalSupply + allowedSupplyAmount).toString()
                    : maxSupply.toString(),
                totalUsdValue: (totalSupply * price).toString(),
                maxUsdValue: baseTokenData.marketId === '12'
                    ? ((totalSupply + allowedSupplyAmount) * price).toString()
                    : (maxSupply * price).toString(),
                aprPercentage: baseTokenData.supplyApr,
                totalReserve: ((totalSupply - totalBorrowed) * price).toString(),
                isSupplyRewardsEnabled: baseTokenData.isSupplyRewardsEnabled,
            },
            progressSize: isSmallMobile ? '80px' : isLargeMobile ? '80px' : '105px',
            progressStrokeWidth: isSmallMobile ? '5px' : isLargeMobile ? '5px' : '7px',
            tooltipContent: {
                apr: 'Annual Percentage Rate for supplying',
                totalReserve: `<div style="margin-bottom: 5px;">The total liquidity of
the asset available <br/> in the platform. This liquidity can be used
<br/> by borrowers to borrow or by suppliers <br/> to
withdraw.</div>`,
            },
            supplyToolTipItems: baseTokenData.supplyToolTipItems,
            isDark,
        };
    };
    const borrowData = () => {
        const totalBorrowed = parseFloat(baseTokenData.totalBorrowed);
        const maxBorrow = parseFloat((marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_limit) || '0');
        const totalSupply = parseFloat(baseTokenData.totalSupply);
        const price = parseFloat(baseTokenData.price);
        const available = parseFloat(baseTokenData.available);
        const borrowProgressPercentage = totalBorrowed || maxBorrow > 0 ? (totalBorrowed / maxBorrow) * 100 : 0;
        return {
            type: 'borrow',
            data: {
                symbol: baseTokenData.symbol,
                progressPercentage: borrowProgressPercentage,
                totalAmount: baseTokenData.totalBorrowed,
                maxAmount: maxBorrow.toString(),
                totalUsdValue: ((totalSupply - available) * price).toString(),
                maxUsdValue: (maxBorrow * price).toString(),
                aprPercentage: baseTokenData.borrowApr,
                isBorrowRewardsEnabled: baseTokenData.isBorrowRewardsEnabled,
            },
            progressSize: isSmallMobile ? '80px' : isLargeMobile ? '80px' : '105px',
            progressStrokeWidth: isSmallMobile ? '5px' : isLargeMobile ? '5px' : '7px',
            tooltipContent: {
                apr: 'Annual Percentage Rate for borrowing',
            },
            borrowToolTipItems: baseTokenData.borrowToolTipItems,
            isDark,
        };
    };
    const supplyStats = () => [
        {
            value: baseTokenData.ltv + '%',
            label: 'Maximum LTV',
        },
        {
            value: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.liquidation_threshold) + '%' || '0%',
            label: 'Liquidation Threshold',
        },
        {
            value: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.close_factor_percentage) + '%' || '0%',
            label: 'Close Factor',
        },
        {
            value: (alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.total_suppliers.toString()) || '0',
            label: 'Total Suppliers',
        },
    ];
    const borrowStats = () => [
        {
            value: baseTokenData.spreadFee + '%' || '0%',
            label: 'Spread Fee',
        },
        {
            value: baseTokenData.borrowWeight || '0',
            label: 'Borrow Weight',
        },
        {
            value: (alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.total_borrowers) || '0',
            label: 'Total Borrowers',
        },
    ];
    const infoIcon = getIcon('infoIcon');
    const prepareTokenForModal = (token) => {
        return Object.assign(Object.assign({}, token), { walletBalance: token.inWallet || '0', totalBorrowedUsd: token.borrowedBalanceUSD || '0', totalSupplyUsd: token.supplyBalanceUSD || '0' });
    };
    const oraclePriceLink = getOraclePriceLink(baseTokenData.symbol);
    const fetchTokensWithPrices = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const tokens = buildTokensList(coinInfoData || undefined, walletBalancesMap);
            setTokens(tokens);
            return tokens;
        }
        catch (error) {
            console.error('Error fetching tokens with prices:', error);
            return [];
        }
    }), [coinInfoData, walletBalancesMap]);
    // Fetch tokens on component mount and when wallet balances change
    useEffect(() => {
        fetchTokensWithPrices();
    }, [fetchTokensWithPrices]);
    // Update tokensRef whenever tokens changes
    useEffect(() => {
        tokensRef.current = tokens;
    }, [tokens]);
    // Get default token for swap
    const defaultTokenForSwap = useMemo(() => {
        const marketInfo = marketsArray.find((m) => m.id === baseTokenData.marketId);
        if (!marketInfo)
            return undefined;
        return tokens.find((t) => (t.symbol !== 'SUI' && t.tokenId === marketInfo.coinType) ||
            (t.symbol === 'SUI' && marketInfo.coinType === '0x2::sui::SUI'));
    }, [tokens, baseTokenData]);
    // Determine if polling should be enabled
    const isPollingEnabled = useMemo(() => {
        return !!(defaultTokenForSwap &&
            selectedSwapSupplyToken &&
            defaultTokenForSwap.symbol !== selectedSwapSupplyToken.symbol &&
            parseFloat(swapAndPayAmount) > 0 &&
            (activeTab === 'Supply' || activeTab === 'Repay'));
    }, [defaultTokenForSwap, selectedSwapSupplyToken, swapAndPayAmount, activeTab]);
    // Use polling hook for swap quotes
    const { newQuoteArrived } = usePollingSwapQuote({
        payAmount: swapAndPayAmount,
        payToken: selectedSwapSupplyToken,
        receiveToken: defaultTokenForSwap || null,
        slippageValue,
        enabled: isPollingEnabled,
        onQuoteUpdate: (quote) => {
            setSwapQuoteObject(quote);
            setIsQuoteStale(false); // Clear stale flag when new quote arrives
        },
        onLoadingChange: (loading) => {
            setIsQuoteLoading(loading);
        },
        // Don't set transaction loading for swap quote fetching - it's not a transaction
        currentQuotePayAmount: swapQuoteObject === null || swapQuoteObject === void 0 ? void 0 : swapQuoteObject.payAmount,
    });
    // Mark quote as stale when amount changes, or clear when amount is 0/empty
    useEffect(() => {
        if (!swapAndPayAmount || parseFloat(swapAndPayAmount) <= 0) {
            setSwapQuoteObject(null);
            setIsQuoteStale(false);
            setIsQuoteLoading(false);
        }
        else if (swapQuoteObject && swapQuoteObject.payAmount !== swapAndPayAmount) {
            // Mark old quote as stale to blur it while new quote is being fetched
            setIsQuoteStale(true);
        }
    }, [swapAndPayAmount, swapQuoteObject]);
    // Flash repay quote polling - polls for collateral → debt token swap
    const isFlashRepayPollingEnabled = useMemo(() => {
        return !!(useCollateral &&
            selectedCollateralToken &&
            defaultTokenForSwap &&
            selectedCollateralToken.symbol !== defaultTokenForSwap.symbol &&
            parseFloat(flashRepaySwapAmount) > 0 &&
            activeTab === 'Repay');
    }, [
        useCollateral,
        selectedCollateralToken,
        defaultTokenForSwap,
        flashRepaySwapAmount,
        activeTab,
    ]);
    const collateralAsPayToken = useMemo(() => {
        if (!selectedCollateralToken)
            return null;
        const balance = parseFloat(selectedCollateralToken.balance || '0');
        const price = selectedCollateralToken.usdValueNum != null && balance > 0
            ? (selectedCollateralToken.usdValueNum / balance).toString()
            : '0';
        return {
            symbol: selectedCollateralToken.symbol,
            icon: selectedCollateralToken.icon,
            walletBalance: selectedCollateralToken.balance,
            price,
            tokenId: selectedCollateralToken.coinType || selectedCollateralToken.symbol,
            decimals: selectedCollateralToken.expo,
        };
    }, [selectedCollateralToken]);
    const { newQuoteArrived: flashRepayNewQuoteArrived } = usePollingSwapQuote({
        payAmount: flashRepaySwapAmount,
        payToken: collateralAsPayToken,
        receiveToken: defaultTokenForSwap || null,
        slippageValue,
        enabled: isFlashRepayPollingEnabled,
        onQuoteUpdate: (quote) => {
            setFlashRepayQuoteObject(quote);
            setIsFlashRepayQuoteStale(false);
        },
        onLoadingChange: (loading) => {
            setIsFlashRepayQuoteLoading(loading);
        },
        currentQuotePayAmount: flashRepayQuoteObject === null || flashRepayQuoteObject === void 0 ? void 0 : flashRepayQuoteObject.payAmount,
    });
    useEffect(() => {
        if (!flashRepaySwapAmount || parseFloat(flashRepaySwapAmount) <= 0) {
            setFlashRepayQuoteObject(null);
            setIsFlashRepayQuoteStale(false);
            setIsFlashRepayQuoteLoading(false);
        }
        else if (flashRepayQuoteObject) {
            const quotedAmt = new BigNumber(flashRepayQuoteObject.payAmount);
            const currentAmt = new BigNumber(flashRepaySwapAmount);
            if (!quotedAmt.isEqualTo(currentAmt)) {
                setIsFlashRepayQuoteStale(true);
            }
        }
    }, [flashRepaySwapAmount, flashRepayQuoteObject]);
    useEffect(() => {
        if (!useCollateral) {
            setFlashRepayQuoteObject(null);
            setFlashRepaySwapAmount('');
            setIsFlashRepayQuoteStale(false);
            setIsFlashRepayQuoteLoading(false);
        }
    }, [useCollateral, selectedCollateralToken]);
    const resetQuoteData = useCallback(() => {
        setSwapQuoteObject(null);
        setSwapAndPayAmount('');
    }, []);
    useEffect(() => {
        resetQuoteData();
        setSelectedSwapSupplyToken(null);
    }, [marketId, symbol, resetQuoteData]);
    const prevActiveTab = useRef(null);
    useEffect(() => {
        if (prevActiveTab.current && prevActiveTab.current !== activeTab) {
            resetQuoteData();
            setSelectedSwapSupplyToken(null);
        }
        prevActiveTab.current = activeTab;
    }, [activeTab, resetQuoteData]);
    const handlePayTokenSelect = (token) => {
        if (!token)
            return;
        setSelectedSwapSupplyToken(token);
        setSwapAndPayAmount('');
        setSlippageValue(getAutoSlippage(token.symbol, symbol !== null && symbol !== void 0 ? symbol : undefined));
        resetQuoteData();
    };
    const formatTokenDataForMarketModal = (token) => {
        if (!token)
            return null;
        return Object.assign(Object.assign({}, baseTokenData), { walletBalance: token.walletBalance, inWallet: token.walletBalance, price: token.price || '0', symbol: token.symbol });
    };
    return (_jsxs("div", { className: "theme-bg-lndBody min-h-screen w-full mx-auto", children: [_jsx(MarketPortfolio, { assetInfo: {
                    symbol: baseTokenData.symbol || '',
                    logo: baseTokenData.icon,
                    name: baseTokenData.symbol,
                }, stats: {
                    totalSupplied: (parseFloat(baseTokenData.totalSupply) * parseFloat(baseTokenData.price)).toString() ||
                        '0',
                    availableLiquidity: (parseFloat(baseTokenData.available) * parseFloat(baseTokenData.price)).toString() ||
                        '0',
                    utilizationRate: convertToKAndM(parseFloat(baseTokenData.utilizationRate)),
                    oraclePrice: baseTokenData.price,
                    oraclePriceLink: oraclePriceLink,
                }, onBackClick: handleBackClick, backArrowIcon: isDark ? getIcon('backArrowWhiteWeb') : getIcon('backArrowBlackWeb'), mobileBackIcon: isDark ? getIcon('backArrowWhiteMobile') : getIcon('backArrowBlackMobile'), documentIcon: isDark ? getIcon('secondaryDocsIcon') : getIcon('blackDocsIcon'), documentLink: `https://suiscan.xyz/mainnet/coin/${(_e = marketsArray.find((m) => m.id === baseTokenData.marketId)) === null || _e === void 0 ? void 0 : _e.coinType}`, documentTooltipText: `<div style="white-space: nowrap;">View token
contract</div>`, oraclePriceTooltipText: "View Oracle Contract", oraclePriceInfoIcon: getIcon('redirectGrayIcon') }), _jsx("div", { className: `${isMobile ? 'px-5 pt-4' : 'px-[50px] pt-12'}
theme-bg-lndBody pb-14 mx-auto`, children: _jsxs("div", { className: "flex flex-col xl:flex-row gap-6 xl:max-w-[1390px]\nlg:max-w-[1100px] md:max-w-full mx-auto", children: [_jsxs("div", { className: `${isMobile
                                ? 'w-full rounded-[16px]'
                                : 'w-full xl:!w-[855px] 2xl:!w-[805px] rounded-[19px]'} h-fit theme-bg-lndPrimary py-4 md:py-6`, children: [_jsx("div", { className: `${isMobile ? 'px-4 text-[16px] mb-4' : 'px-10 text-[18px] mb-4'} font-InterSemiBold theme-text-lndPrimary`, children: "Reserve Status & Configuration" }), _jsx("div", { className: "w-full border theme-border-lndTable" }), _jsx("div", { className: `${isMobile ? 'px-5 py-4' : 'md:px-10 md:py-8'}`, children: _jsx(MarketInfoData, Object.assign({}, supplyData())) }), _jsx("div", { className: `${isMobile ? 'mb-[18px]' : 'mb-5'} px-4 md:px-10`, children: _jsx(SupplyChart, { data: supplyChartData, isDark: isDark, isLoading: (alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.supply_apr_graph) === undefined }) }), _jsx("div", { className: `${isMobile ? 'px-4 mb-6' : 'px-10 mb-8'}`, children: _jsx("div", { className: `${isMobile ? 'gap-3' : 'gap-[17px]'} flex flex-wrap`, children: supplyStats().map((stat, index) => (_jsx(StatCard, { stats: [stat], width: "w-[47%] xs375:w-[48%] md:w-[130px] lg:w-[158px] xl:w-[166px]", height: `${isMobile ? 'h-[71px]' : 'md:h-fit'}`, background: isDark ? 'bg-[#282B2F]' : 'bg-[#F7F7F8]', valueFontSize: "text-base lg:text-xl", labelFontSize: "text-[9px] md:text-[10px] xl:text-xs", padding: "p-4 md:p-5", rounded: "rounded-2xl lg:rounded-3xl", valueFontColor: "theme-text-lndPrimary", labelFontColor: "theme-text-lndSecondary", infoIcon: infoIcon, hoverTexts: {
                                                'Maximum LTV': `<div style="margin-bottom: 5px;">The maximum
                        percentage of your <br/> supplied asset's value that <br/> can be
                        borrowed.</div>`,
                                                'Liquidation Threshold': `<div style="margin-bottom: 5px;">The borrow
                        limit beyond which your <br/> position is considered at risk and <br/>
                        becomes eligible for liquidation.</div>`,
                                                'Close Factor': `<div style="margin-bottom: 5px;">The percentage of an
                        outstanding <br/> loan that can be liquidated in a <br/> single
                        transaction.</div>`,
                                            } }, index))) }) }), _jsx("div", { className: "w-full border theme-border-lndTable" }), _jsx("div", { className: `${isMobile ? 'px-5 py-4' : 'px-10 py-8'}`, children: _jsx(MarketInfoData, Object.assign({}, borrowData())) }), _jsx("div", { className: `${isMobile ? 'mb-[18px]' : 'mb-5'} px-4 md:px-10`, children: _jsx(BorrowChart, { data: borrowChartData, isDark: isDark, isLoading: (alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.borrow_apr_graph) === undefined }) }), _jsx("div", { className: `${isMobile ? 'px-4 mb-6' : 'px-10 mb-8'}`, children: _jsx("div", { className: `${isMobile ? 'gap-3' : 'gap-[17px]'} flex flex-wrap`, children: borrowStats().map((stat, index) => (_jsx(StatCard, { stats: [{ value: stat.value.toString(), label: stat.label }], width: "w-[47%] xs375:w-[48%] md:w-[130px] lg:w-[158px] xl:w-[166px]", height: `${isMobile ? 'h-[71px]' : 'md:h-fit'}`, background: isDark ? 'bg-[#282B2F]' : 'bg-[#F7F7F8]', valueFontSize: "text-base lg:text-xl", labelFontSize: "text-[9px] md:text-[10px] xl:text-xs", padding: "p-4 md:p-5", rounded: "rounded-2xl lg:rounded-3xl", valueFontColor: "theme-text-lndPrimary", labelFontColor: "theme-text-lndSecondary", infoIcon: infoIcon, hoverTexts: {
                                                'Spread Fee': `<div style="margin-bottom: 5px;">A percentage of the
borrower's <br/> interest kept by the protocol, <br/> contributing to
platform revenue.</div>`,
                                                'Borrow Weight': `<div style="margin-bottom: 5px;">It is a risk
multiplier applied to the<br/>borrowed amount to calculate
your<br/>borrow limit and liquidation threshold.</div>`,
                                            } }, index))) }) }), _jsx("div", { className: `${isMobile ? 'mt-0 mb-6' : 'mt-8 mb-7'} w-full border theme-border-lndTable ` }), _jsx("div", { className: `${isMobile
                                        ? 'px-4 text-[16px] mb-4'
                                        : 'px-10 text-lg md:text-xl font-InterSemiBold mb-7'} font-InterSemiBold theme-text-lndPrimary`, children: "Interest Rate Model" }), _jsx("div", { className: "px-4 md:px-10 mb-8", children: _jsx(StatCard, { stats: [
                                            {
                                                value: `${convertToKAndM(parseFloat(baseTokenData.utilizationRate))}%`,
                                                label: 'Utilization Rate',
                                            },
                                        ], width: "w-[47%] xs375:w-[48%] md:w-[130px] lg:w-[158px] xl:w-[166px]", height: `${isMobile ? 'h-[71px]' : 'md:h-fit'}`, background: isDark ? 'bg-[#282B2F]' : 'bg-[#F7F7F8]', valueFontSize: "text-base lg:text-xl", labelFontSize: "text-[9px] md:text-[10px] xl:text-xs", padding: "p-4 md:p-5", rounded: "rounded-2xl lg:rounded-3xl", valueFontColor: "theme-text-lndPrimary", labelFontColor: "theme-text-lndSecondary", infoIcon: infoIcon }) }), _jsx("div", { className: `${isMobile ? 'mt-4' : 'mt-0'} px-4 md:px-10 mb-7`, children: _jsx(UtilizationChart, { data: utilizationRateData, currentMarker: baseTokenData.utilizationRate || '0', optimalMarker: alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.optimal_utilization_rate, isDark: isDark, isLoading: (alphalendMarketDetailsData === null || alphalendMarketDetailsData === void 0 ? void 0 : alphalendMarketDetailsData.utilization_rate_graph) === undefined }) })] }), !isMobile && (_jsx("div", { className: "w-[510px] h-auto mx-auto xl:mx-[unset]", children: _jsx(TokenActionModal, { isOpen: true, token: prepareTokenForModal(baseTokenData), onClose: () => {
                                    resetQuoteData();
                                }, onSubmit: handleSubmit, activeTab: activeTab, setActiveTab: setActiveTab, setIsTransactionLoading: setIsTransactionLoading, isTransactionLoading: isTransactionLoading, isWalletConnected: !!address, rightArrow: isDark ? getIcon('darkThemeRightArrow') : getIcon('rightArrow'), inline: true, resetInputTrigger: resetInputTrigger, clearSearchIcon: getIcon('clearSearchIcon'), searchIcon: themeName === 'dark' ? getIcon('searchDark') : getIcon('defaultThemeSearchIcon'), backIcon: themeName === 'dark' ? getIcon('whiteBackIcon') : getIcon('blackBackIcon'), payToken: tokens.find((t) => baseTokenData !== null && t.symbol === baseTokenData.symbol), tokens: tokens, onPayTokenSelect: handlePayTokenSelect, defaultToken: tokens.find((t) => baseTokenData !== null && t.symbol === baseTokenData.symbol), selectedToken: selectedSwapSupplyToken || undefined, setSwapAndPayAmount: setSwapAndPayAmount, selectedTokenInModalFormat: formatTokenDataForMarketModal(selectedSwapSupplyToken), swapQuoteObject: swapQuoteObject, resetQuoteData: resetQuoteData, isMarketDetailsPage: true, viceVersaIcon: themeName === 'dark'
                                    ? getIcon('viceVersaIconWhite')
                                    : getIcon('viceVersaIconBlack'), downArrow: themeName === 'dark' ? getIcon('whiteDownArrow') : getIcon('blackDownArrow'), slippageIcon: themeName === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('blackSlippageIcon'), slippageValue: slippageValue, onSlippageChange: setSlippageValue, zapRightArrow: themeName === 'dark' ? getIcon('darkThemeRightArrow') : getIcon('rightArrow'), newQuoteArrived: newQuoteArrived, isQuoteStale: isQuoteStale, isQuoteLoading: isQuoteLoading, collateralTokens: collateralTokens, onCollateralTokenSelect: setSelectedCollateralToken, selectedCollateralToken: selectedCollateralToken !== null && selectedCollateralToken !== void 0 ? selectedCollateralToken : undefined, isFlashRepaySupported: isFlashRepaySupported, flashRepayFeeDecimal: naviFlashLoanFee, flashRepayMaxRepayAmount: flashRepayMaxRepayAmount !== null && flashRepayMaxRepayAmount !== void 0 ? flashRepayMaxRepayAmount : null, flashRepayQuoteObject: flashRepayQuoteObject, isFlashRepayQuoteStale: isFlashRepayQuoteStale, isFlashRepayQuoteLoading: isFlashRepayQuoteLoading, flashRepayNewQuoteArrived: flashRepayNewQuoteArrived, onFlashRepaySwapAmountChange: setFlashRepaySwapAmount, onUseCollateralChange: setUseCollateral }) }))] }) }), isMobile && (_jsx(MobileActionButtons, { token: prepareTokenForModal(baseTokenData), isWalletConnected: !!address, onSupplyClick: handleMobileSupplyClick, onBorrowClick: handleMobileBorrowClick, isSupplyDisabled: isTransactionLoading, isBorrowDisabled: isTransactionLoading, walletConnectFunctionality: walletConnectFunctionality })), isMobile && (_jsx(TokenActionModal, { isOpen: isMobileModalOpen, token: prepareTokenForModal(baseTokenData), onClose: handleMobileModalClose, onSubmit: handleSubmit, activeTab: activeTab, setActiveTab: setActiveTab, setIsTransactionLoading: setIsTransactionLoading, isTransactionLoading: isTransactionLoading, isWalletConnected: !!address, rightArrow: isDark ? getIcon('darkThemeRightArrow') : getIcon('rightArrow'), inline: false, resetInputTrigger: resetInputTrigger, clearSearchIcon: getIcon('clearSearchIcon'), searchIcon: themeName === 'dark' ? getIcon('searchDark') : getIcon('defaultThemeSearchIcon'), backIcon: themeName === 'dark' ? getIcon('whiteBackIcon') : getIcon('blackBackIcon'), payToken: (() => {
                    const marketInfo = marketsArray.find((m) => m.id === baseTokenData.marketId);
                    return marketInfo
                        ? tokens.find((t) => t.tokenId === marketInfo.coinType)
                        : tokens[0];
                })(), tokens: tokens, onPayTokenSelect: handlePayTokenSelect, defaultToken: (() => {
                    const marketInfo = marketsArray.find((m) => m.id === baseTokenData.marketId);
                    return marketInfo
                        ? tokens.find((t) => t.tokenId === marketInfo.coinType)
                        : tokens[0];
                })(), selectedToken: selectedSwapSupplyToken || undefined, setSwapAndPayAmount: setSwapAndPayAmount, selectedTokenInModalFormat: formatTokenDataForMarketModal(selectedSwapSupplyToken), swapQuoteObject: swapQuoteObject, resetQuoteData: resetQuoteData, viceVersaIcon: themeName === 'dark' ? getIcon('viceVersaIconWhite') : getIcon('viceVersaIconBlack'), downArrow: themeName === 'dark' ? getIcon('whiteDownArrow') : getIcon('blackDownArrow'), isMarketDetailsPage: true, slippageIcon: themeName === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('blackSlippageIcon'), slippageValue: slippageValue, onSlippageChange: setSlippageValue, zapRightArrow: themeName === 'dark' ? getIcon('darkThemeRightArrow') : getIcon('rightArrow'), newQuoteArrived: newQuoteArrived, isQuoteStale: isQuoteStale, isQuoteLoading: isQuoteLoading, collateralTokens: collateralTokens, onCollateralTokenSelect: setSelectedCollateralToken, selectedCollateralToken: selectedCollateralToken !== null && selectedCollateralToken !== void 0 ? selectedCollateralToken : undefined, isFlashRepaySupported: isFlashRepaySupported, flashRepayFeeDecimal: naviFlashLoanFee, flashRepayMaxRepayAmount: flashRepayMaxRepayAmount !== null && flashRepayMaxRepayAmount !== void 0 ? flashRepayMaxRepayAmount : null, flashRepayQuoteObject: flashRepayQuoteObject, isFlashRepayQuoteStale: isFlashRepayQuoteStale, isFlashRepayQuoteLoading: isFlashRepayQuoteLoading, flashRepayNewQuoteArrived: flashRepayNewQuoteArrived, onFlashRepaySwapAmountChange: setFlashRepaySwapAmount, onUseCollateralChange: setUseCollateral })), _jsx(SwapButton, { isSwapModalOpen: isSwapModalOpen, onClick: () => setIsSwapModalOpen(true), themeName: themeName, isTokenActionModalOpen: isMobileModalOpen }), _jsx(SwapModal, { isOpen: isSwapModalOpen, onClose: () => setIsSwapModalOpen(false), onNotification: handleSwapNotification, loading: isTransactionLoading, setLoading: setIsTransactionLoading, walletConnectFunctionality: walletConnectFunctionality, isWalletConnected: !!address, theme: themeName }), _jsx(NotificationComp, { info: notificationInfo, isOpen: isNotificationOpen, setIsOpen: setIsNotificationOpen })] }));
};
export default MarketDetails;
